import Dexie, { type EntityTable } from 'dexie';

export interface User {
  id: number;
  authId?: string;
  name: string;
  responsibleName?: string;
  jobTitle?: string;
  teamResponsibleNames?: string[];
  teamEmails?: string[];
  teamProductCodes?: string[];
  email: string;
  passwordHash: string;
  phone: string;
  role: string;
  division?: string;
  managerEmail?: string;
  forcePasswordChange?: boolean;
  productCodes?: string[];
  active?: boolean;
  createdAt: string;
}

export interface Cliente {
  id: number;
  ativo?: boolean; // false = inativo no CIGAM: não pode ser usado em cotações
  codigo: string;
  nome: string;
  uf: string;
  cidade: string;
  alerta: string;
  condicaoPagamento: string;
  prazo: number | null;
  frete: string;
  freteManual?: string;
  cnpj?: string;
  pontoFocalNome?: string;
  pontoFocalEmail?: string;
  pontoFocalTelefone?: string;
  enderecoEmpresa?: string;
  responsavel?: string;
  divisao?: string;
}

export interface Material {
  id: number;
  codigo: string;
  descricao: string;
  origemFiscal: string;
  custoReposicao: number;
  ipi: number;
  icmsRs: number;
  codigoOrigem: string;
  estoque: number | null;
  unidadeMedida: string;
  responsaveis?: readonly string[];
  divisoes?: readonly string[];
  ativo?: boolean; // false = Inativo no CIGAM: não pode ser usado
  contaGerencial?: string; // divisão do produto no CIGAM
}

export interface CotacaoItem {
  materialCodigo: string;
  materialDescricao: string;
  unidadeMedida: string;
  custoReposicao: number;
  icms: number;
  ipi: number;
  fator: number;
  precoNetMinimo: number;
  precoComImpostosMinimo: number;
  precoNegociado: number;
  precoNegociadoComImpostos?: number;
  margemRealizada: number;
  precoUnitarioConvertido: number;
  peso?: number; // kg, quando o vendedor informou
}

export interface Cotacao {
  id: number;
  clienteCodigo: string;
  clienteNome: string;
  vendedor: string;
  divisao?: string;
  moeda: string;
  itens: CotacaoItem[];
  total: number;
  totalBrl: number;
  margemMedia: number;
  beneficioFiscal: boolean;
  isentoIpi: boolean;
  isentoIcms: boolean;
  isentoPisCofins: boolean;
  condicaoPagamento: string;
  frete?: string;
  cotacaoUsd: number;
  cotacaoEur: number;
  margemAlvo: number;
  createdAt: string;
  pendingSync?: boolean; // só neste navegador (ainda não enviada à nuvem)
}

export interface Viagem {
  id: number;
  data: string;
  vendedor: string;
  destino: string;
  motivo: string;
  valor: number;
  observacoes: string;
}

export interface Config {
  id: number;
  cotacaoUsd: number;
  cotacaoEur: number;
  margemAlvo: number;
  cambioAtualizadoEm?: string;
  cambioDataFonte?: string;
}

const db = new Dexie('zsb-precificacao') as Dexie & {
  users: EntityTable<User, 'id'>;
  clientes: EntityTable<Cliente, 'id'>;
  materiais: EntityTable<Material, 'id'>;
  cotacoes: EntityTable<Cotacao, 'id'>;
  viagens: EntityTable<Viagem, 'id'>;
  config: EntityTable<Config, 'id'>;
};

db.version(1).stores({
  users: '++id, email',
  clientes: '++id, codigo, nome, uf, cidade',
  materiais: '++id, codigo, descricao, origemFiscal, codigoOrigem',
  cotacoes: '++id, clienteCodigo, vendedor, createdAt',
  viagens: '++id, data, vendedor',
  config: '++id',
});

db.version(2).stores({
  users: '++id, email',
  clientes: '++id, codigo, nome, uf, cidade',
  materiais: '++id, codigo, descricao, origemFiscal, codigoOrigem',
  cotacoes: '++id, clienteCodigo, vendedor, createdAt',
  viagens: '++id, data, vendedor',
  config: '++id',
}).upgrade(async (tx) => {
  await tx.table('clientes').clear();
  await tx.table('materiais').clear();
});

db.version(3).stores({
  users: '++id, email',
  clientes: '++id, codigo, nome, uf, cidade',
  materiais: '++id, codigo, descricao, origemFiscal, codigoOrigem',
  cotacoes: '++id, clienteCodigo, vendedor, createdAt',
  viagens: '++id, data, vendedor',
  config: '++id',
}).upgrade(async (tx) => {
  await tx.table('clientes').clear();
  await tx.table('materiais').clear();
  const demo = await tx.table('users').where('email').equals('demo@zsb.com.br').first();
  if (demo) await tx.table('users').update(demo.id, { role: 'Administrador' });
});

db.version(4).stores({
  users: '++id, email, role, managerEmail',
  clientes: '++id, codigo, nome, uf, cidade',
  materiais: '++id, codigo, descricao, origemFiscal, codigoOrigem',
  cotacoes: '++id, clienteCodigo, vendedor, createdAt',
  viagens: '++id, data, vendedor',
  config: '++id',
});

export default db;
